import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import { uploadDocumentFile } from "./limsForskrifterUpload"

// QA decisions (PRD-003 Area 4 / SPEC-010; SPEC-021 B7: reimplemented against the SP-E ATOMIC
// contract). A QA decision is the signed disposition of one OOS:
//
//   POST /lims/qaDecisions { input:{oosId, sampleId, analysisId, sampleLabel, analysisLabel,
//                            batchLot, decision, comment, reportFileKey?, reportFileName?},
//                            password, reason? }
//
// The backend applies the WHOLE disposition under ONE QA e-signature in ONE transaction
// ("qa" capability, 4-eyes ≠ the OOS raiser): it writes the decision record, CLOSES the linked
// OOS (stamping qaDecisionId + disposition, unflagging the sample when it was the last open
// OOS) and — for "batch-rejected" with a lot — creates the batch hold. The FE must NOT follow
// up with its own oos.close or batchHold call for this flow.
//
// "may-reanalyse": the FE creates the rerun sample itself (samples.createSample via
// limsReruns.createRerun) and POSTs the /lims/reruns link afterwards (the backend dedups) —
// per B-D5 reruns stay FE-created entities.
//
// Record shape (server dump):
//   { id, oosId, sampleId, analysisId, sampleLabel, analysisLabel, batchLot,
//     decision: "may-reanalyse" | "batch-rejected", comment,
//     reportFileKey, reportFileName, signedBy, signedAt, signReason }
const initialState = { items: [], isLoading: false }

export const QA_DECISION_TYPES = {
  MAY_REANALYSE: "may-reanalyse",
  BATCH_REJECTED: "batch-rejected",
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsQaDecisions/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsQaDecisions/fetch":
      return { items: action.res, isLoading: false }
    case "limsQaDecisions/decide": {
      if (!action.res) return state
      const exists = state.items.some((d) => d.id === action.res.id)
      return {
        ...state,
        items: exists
          ? state.items.map((d) => (d.id === action.res.id ? action.res : d))
          : [...state.items, action.res],
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsQaDecisions/fetch", (_dispatch) =>
  limsRequest("GET", "/lims/qaDecisions").then((res) => res.items)
)

// Record the signed QA disposition for one OOS — ATOMICALLY closing the OOS (and, for
// "batch-rejected" with a lot, holding the lot) under the single QA signature whose password
// rides at the request-body top level. An optional `reportFile` (the QA investigation report)
// is uploaded to the qaReports area first; only its server-minted key + name are persisted.
//
// `args`: { oos, decision, comment, batchLot, sampleLabel?, analysisLabel?, signature,
//           reportFile? }. Returns the created decision record; callers refresh limsOos (+
// limsBatchHold for batch-rejected) afterwards so the store reflects the server-side close.
export const decide = asyncAction(
  "limsQaDecisions/decide",
  (_dispatch, { oos, decision, comment, batchLot, sampleLabel, analysisLabel, signature, reportFile } = {}) => {
    if (!oos || !oos.id) return Promise.reject(new Error("limsQaDecisions.decide: no OOS"))
    const upload = reportFile
      ? uploadDocumentFile(reportFile, { prefix: "qaReports" })
      : Promise.resolve(null)
    return upload.then((descriptor) => {
      const input = {
        oosId: oos.id,
        sampleId: oos.sampleId || null,
        analysisId: oos.analysisId || null,
        sampleLabel: sampleLabel || oos.sampleLabel || "",
        analysisLabel: analysisLabel || oos.analysisLabel || "",
        batchLot: batchLot || null,
        decision:
          decision === QA_DECISION_TYPES.BATCH_REJECTED
            ? QA_DECISION_TYPES.BATCH_REJECTED
            : QA_DECISION_TYPES.MAY_REANALYSE,
        comment: comment || "",
        reportFileKey: descriptor ? descriptor.key : null,
        reportFileName: descriptor ? descriptor.name : null,
      }
      return limsRequest("POST", "/lims/qaDecisions", {
        input,
        password: (signature && signature.password) || undefined,
        reason: (signature && signature.reason) || undefined,
      }).then((res) => res.item)
    })
  }
)

// The signed decision for a given OOS (or undefined).
export const selectDecisionForOos = (state, oosId) =>
  state.limsQaDecisions.items.find((d) => d.oosId === oosId)

export const selectAllDecisions = (state) => state.limsQaDecisions.items
